import { InMemoryCache, type TypePolicies } from "@apollo/client";

import { appInfoTypePolicy } from "../../camkit/apps/app-details/api/cache/appInfoTypePolicy";
import { GroupsPagination } from "../../camkit/groups/api/cache/GroupsPagination";
import { groupWithConnectionsTypePolicy } from "../../camkit/groups/api/cache/GroupsWithConnectionCache";
import { GroupsRepositoryV2 } from "../../camkit/groups/model/GroupsRespositoryV2";
import { AffectedGroupsRepository, LensSourcesRepository } from "../../camkit/lens-source/api/cache";
import { LensesRepositoryV2 } from "../../camkit/lenses/models/LensesRepositoryV2";
import { OrganizationsRepository } from "../../camkit/organizations/model/OrganizationsRepository";
import {
    type AppInfo,
    type CameraKitLens,
    type GetLensCreatorPayoutTosAcceptedVersionResponse,
    type GroupLensConnection,
    type GroupWithConnections,
    type LensAccountId,
    type LensCompoundId,
    type LensDestination,
    type LensWithConnections,
    type MyLensesLens,
    type QrCode,
    type ResourceContainer,
    type Snapcode,
    type Tos,
    type TosMetadata,
} from "../../gql-generated/types";
import { payoutStatsCacheConfig } from "../../lens-insights/api/apollo-cache/payoutStatsCache";
import { cacheLensesConfig } from "../../lenses/api/apollo-cache";
import { cacheRemoteAssetsConfig } from "../../remote-assets/api/apollo-cache";

// Typenames for type safety
const appInfo: AppInfo["__typename"] = "AppInfo";
const cameraKitLens: CameraKitLens["__typename"] = "CameraKitLens";
const getLensCreatorPayoutTosAcceptedVersionResponse: GetLensCreatorPayoutTosAcceptedVersionResponse["__typename"] =
    "GetLensCreatorPayoutTosAcceptedVersionResponse";
const groupLensConnection: GroupLensConnection["__typename"] = "GroupLensConnection";
const groupWithConnections: GroupWithConnections["__typename"] = "GroupWithConnections";
const lensAccountId: LensAccountId["__typename"] = "LensAccountID";
const lensCompoundId: LensCompoundId["__typename"] = "LensCompoundId";
const lensDestination: LensDestination["__typename"] = "LensDestination";
const lensWithConnections: LensWithConnections["__typename"] = "LensWithConnections";
const myLensesLens: MyLensesLens["__typename"] = "MyLensesLens";
const qrCode: QrCode["__typename"] = "QrCode";
const resourceContainer: ResourceContainer["__typename"] = "ResourceContainer";
const snapcode: Snapcode["__typename"] = "Snapcode";
const tos: Tos["__typename"] = "Tos";
const tosMetadata: TosMetadata["__typename"] = "TosMetadata";

// Singletons are GraphQL types with no unique key field (i.e. they are only single instance)
const singletons = [getLensCreatorPayoutTosAcceptedVersionResponse];

const typePolicies: TypePolicies = {
    Query: {
        fields: {
            ...cacheLensesConfig,
            ...cacheRemoteAssetsConfig,
            ...payoutStatsCacheConfig,
            ...GroupsPagination.cache.typePolicy,
        },
    },
    [lensAccountId]: {
        keyFields: false,
        merge: true,
    },
    [lensDestination]: {
        keyFields: ["id", "lensId"],
    },
    [myLensesLens]: {
        fields: {
            // Some pages write only a subset of these nested objects.
            lensAccountId: {
                merge: true,
            },
            snapcode: {
                merge: true,
            },
            qrCode: {
                merge: true,
            },
        },
    },
    [qrCode]: {
        keyFields: false,
        merge: true,
    },
    [resourceContainer]: {
        keyFields: ["id", "kind"],
    },
    [snapcode]: {
        keyFields: false,
        merge: true,
    },
    ...Object.fromEntries(singletons.map((singleton) => [singleton, { keyFields: [] }])),
    [tos]: {
        keyFields: ["key"],
    },
    [tosMetadata]: {
        keyFields: false, // store this object under parent object
    },
    [groupLensConnection]: {
        keyFields: ["connectionId"],
    },
    [groupWithConnections]: {
        keyFields: ["group", ["id"]],
        fields: groupWithConnectionsTypePolicy,
    },
    [lensWithConnections]: {
        keyFields: ["lens", ["compoundId", ["id", "reference"]]],
    },
    [cameraKitLens]: {
        keyFields: ["compoundId", ["id", "reference"]],
    },
    [lensCompoundId]: {
        keyFields: ["id", "reference"],
    },
    [appInfo]: {
        keyFields: ["appId"],
        fields: appInfoTypePolicy,
    },
};

export const createCache = (): InMemoryCache =>
    new InMemoryCache({
        typePolicies,
    });

export const cache = createCache();

// Seed default cache entries for camkit repositories. Runs once at module load.
OrganizationsRepository.initDefaults(cache);
GroupsRepositoryV2.initDefaults(cache);
LensesRepositoryV2.initDefaults(cache);
LensSourcesRepository.initDefaults(cache);
AffectedGroupsRepository.initDefaults(cache);
